import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { TextField } from "@mui/material";
import {
  ACHIEVEMENTS,
  DEFAULT_GAMIFICATION,
  readGamificationFromSettings,
} from "../utils/gamification";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";

const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

export default function GamificationSettings({ value, onChange, embedded = false }) {
  const gamification = readGamificationFromSettings({ gamification: value });

  const patch = (next) => {
    if (next.enabled === false) {
      onChange({
        ...gamification,
        ...next,
        showLeaderboard: false,
        showAchievements: false,
        showStreaks: false,
        certificateEnabled: false,
        monthlyChallenge: {
          ...gamification.monthlyChallenge,
          ...(next.monthlyChallenge ?? {}),
          enabled: false,
        },
      });
      return;
    }

    onChange({
      ...gamification,
      ...next,
      monthlyChallenge: {
        ...gamification.monthlyChallenge,
        ...(next.monthlyChallenge ?? {}),
      },
    });
  };

  const body = (
    <>
      {!embedded ? (
        <>
          <h3 className="app-settings-card__title">Геймификация</h3>
          <p className="app-settings-toggle__hint">
            XP, рейтинг, достижения и челленджи в боте для сотрудников.
          </p>
        </>
      ) : null}

      <div className="app-settings-client-grid">
        <div className="app-settings-toggle">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={gamification.enabled !== false}
                onChange={(event) => patch({ enabled: event.target.checked })}
              />
            }
            label="Включить геймификацию"
          />
        </div>

        {gamification.enabled === false ? (
          <div className="app-gamification-settings__collapsed">
            <div>Рейтинг и достижения</div>
            <div>Серии дней и сертификат</div>
            <div>Начисление XP</div>
            <div>Челлендж месяца</div>
            <div>Достижения в проекте</div>
          </div>
        ) : (
          <>
        <div className="app-settings-toggle">
          <FormControlLabel
            control={
              <Switch
                size="small"
                disabled={gamification.enabled === false}
                checked={gamification.showLeaderboard !== false}
                onChange={(event) =>
                  patch({ showLeaderboard: event.target.checked })
                }
              />
            }
            label="Показывать рейтинг"
          />
        </div>

        <div className="app-settings-toggle">
          <FormControlLabel
            control={
              <Switch
                size="small"
                disabled={gamification.enabled === false}
                checked={gamification.showAchievements !== false}
                onChange={(event) =>
                  patch({ showAchievements: event.target.checked })
                }
              />
            }
            label="Достижения"
          />
        </div>

        <div className="app-settings-toggle">
          <FormControlLabel
            control={
              <Switch
                size="small"
                disabled={gamification.enabled === false}
                checked={gamification.showStreaks !== false}
                onChange={(event) => patch({ showStreaks: event.target.checked })}
              />
            }
            label="Серии дней"
          />
        </div>

        <div className="app-settings-toggle">
          <FormControlLabel
            control={
              <Switch
                size="small"
                disabled={gamification.enabled === false}
                checked={gamification.certificateEnabled !== false}
                onChange={(event) =>
                  patch({ certificateEnabled: event.target.checked })
                }
              />
            }
            label="Сертификат при завершении"
          />
        </div>

        <div className="app-settings-field">
          <label className="app-settings-field__label">XP за правильный ответ</label>
          <TextField
            type="number"
            size="small"
            disabled={gamification.enabled === false}
            value={gamification.xpPerCorrect ?? DEFAULT_GAMIFICATION.xpPerCorrect}
            onChange={(event) =>
              patch({ xpPerCorrect: Number(event.target.value) || 0 })
            }
            sx={fieldSx}
          />
        </div>

        <div className="app-settings-field">
          <label className="app-settings-field__label">XP за завершение тренажёра</label>
          <TextField
            type="number"
            size="small"
            disabled={gamification.enabled === false}
            value={
              gamification.xpCompletionBonus ??
              DEFAULT_GAMIFICATION.xpCompletionBonus
            }
            onChange={(event) =>
              patch({ xpCompletionBonus: Number(event.target.value) || 0 })
            }
            sx={fieldSx}
          />
        </div>

        <div className="app-settings-field">
          <label className="app-settings-field__label">XP за завершение сценария</label>
          <TextField
            type="number"
            size="small"
            disabled={gamification.enabled === false}
            value={
              gamification.xpScenarioComplete ??
              DEFAULT_GAMIFICATION.xpScenarioComplete
            }
            onChange={(event) =>
              patch({ xpScenarioComplete: Number(event.target.value) || 0 })
            }
            sx={fieldSx}
          />
        </div>

        <div className="app-settings-field app-settings-field--wide">
          <label className="app-settings-field__label">Текст сертификата</label>
          <TextField
            multiline
            minRows={4}
            fullWidth
            disabled={gamification.enabled === false}
            value={gamification.certificateText ?? DEFAULT_GAMIFICATION.certificateText}
            onChange={(event) => patch({ certificateText: event.target.value })}
            sx={fieldSx}
          />
          <div className="app-settings-certificate-vars">
            <p className="app-settings-certificate-vars__title">
              Переменные в фигурных скобках подставляются автоматически при выдаче сертификата в мессенджере:
            </p>
            <ul className="app-settings-certificate-vars__list">
              <li>
                <code>{"{name}"}</code> — имя ученика из профиля Telegram или MAX
              </li>
              <li>
                <code>{"{title}"}</code> — название сценария из настроек проекта
              </li>
              <li>
                <code>{"{score}"}</code> — результат прохождения (баллы или итог тренажёра)
              </li>
              <li>
                <code>{"{rank}"}</code>, <code>{"{total}"}</code> — место в рейтинге и число участников
              </li>
              <li>
                <code>{"{level}"}</code>, <code>{"{xp}"}</code> — уровень и опыт из системы мотивации
              </li>
              <li>
                <code>{"{date}"}</code> — дата выдачи сертификата
              </li>
            </ul>
            <p className="app-settings-certificate-vars__note">
              Заполнять вручную ничего не нужно — просто оставьте переменные в тексте там, где хотите видеть эти данные.
            </p>
          </div>
        </div>

        <div className="app-settings-toggle app-settings-field--wide">
          <FormControlLabel
            control={
              <Switch
                size="small"
                disabled={gamification.enabled === false}
                checked={gamification.monthlyChallenge?.enabled === true}
                onChange={(event) =>
                  patch({
                    monthlyChallenge: {
                      ...gamification.monthlyChallenge,
                      enabled: event.target.checked,
                    },
                  })
                }
              />
            }
            label="Челлендж месяца"
          />
        </div>

        {gamification.monthlyChallenge?.enabled ? (
          <>
            <div className="app-settings-field">
              <label className="app-settings-field__label">Цель (завершений в месяц)</label>
              <TextField
                type="number"
                size="small"
                value={
                  gamification.monthlyChallenge?.targetCompletions ??
                  DEFAULT_GAMIFICATION.monthlyChallenge.targetCompletions
                }
                onChange={(event) =>
                  patch({
                    monthlyChallenge: {
                      ...gamification.monthlyChallenge,
                      targetCompletions: Number(event.target.value) || 1,
                    },
                  })
                }
                sx={fieldSx}
              />
            </div>
            <div className="app-settings-field app-settings-field--wide">
              <label className="app-settings-field__label">Текст приза</label>
              <TextField
                fullWidth
                size="small"
                value={gamification.monthlyChallenge?.prizeText ?? ""}
                onChange={(event) =>
                  patch({
                    monthlyChallenge: {
                      ...gamification.monthlyChallenge,
                      prizeText: event.target.value,
                    },
                  })
                }
                placeholder="Например: сертификат или приз от HR"
                sx={fieldSx}
              />
            </div>
          </>
        ) : null}
          </>
        )}
      </div>

      {gamification.enabled !== false ? (
        <div className="app-settings-field app-settings-field--wide">
          <span className="app-settings-field__label">Достижения в проекте</span>
          <ul className="app-gamification-achievements">
            {ACHIEVEMENTS.map((item) => (
              <li key={item.id}>
                {item.icon} <strong>{item.title}</strong> — {item.description}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return <div className="app-gamification-settings--embedded">{body}</div>;
  }

  return <section className="app-settings-card">{body}</section>;
}
